import { useState } from "react";

import { Colors, Icon } from "@blueprintjs/core";
import { Link, useNavigate } from "react-router";

import {
  isProgramLead,
  isWorkspaceAdmin,
} from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { Container } from "@/components/pieces/bootstrap";
import { SUBTEXT_COLOR } from "@/css/constants";
import { ProgramStatus } from "@/graphql";
import { MainProgram, ProgramDialog } from "@/types/programs";

import { ModifyProgramDialogs } from "./ModifyProgramDialogs";
import { StyledLink } from "./ProgramComponentResponse";
import { ProgramMenu } from "./ProgramMenu";
import { ProgramDateDisplay, ProgramStatusBadge } from "./ProgramStatus";
import {
  getProgramTabs,
  PROGRAM_TABS_CONFIG,
  ProgramTabs,
} from "./ProgramTabs";

interface ProgramDetailsHeaderProps {
  program: MainProgram;
  initialTab?: string;
  refetchProgram: () => void;
}

export function ProgramDetailsHeader({
  program,
  initialTab = PROGRAM_TABS_CONFIG[0].key,
  refetchProgram,
}: ProgramDetailsHeaderProps) {
  const { name } = program;
  const [openMenu, setOpenMenu] = useState(false);
  const tabs = getProgramTabs(program?.id);
  const [dialogOpen, setDialogOpen] = useState<ProgramDialog>();
  const navigate = useNavigate();

  const isAdmin = isWorkspaceAdmin();
  const isLead = isProgramLead(program?.id);
  const isShowMenu = isAdmin || isLead;

  return (
    <Box className="bbs" pt={[2, 4]} bg={Colors.WHITE}>
      <Container>
        <Box mb={3}>
          <Link
            to="/programs"
            style={{
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <Icon icon="chevron-left" size={14} />{" "}
            <span style={{ fontSize: 15 }}>All Programs</span>
          </Link>
        </Box>

        <Flex mt={2} alignItems={"center"} flexDirection={"row"}>
          <Flex flexDirection="column" flex={1} width={"auto"}>
            <Box mb={1}>
              <h1 style={{ fontSize: 28, margin: 0 }}>
                <StyledLink to={`/programs/${program?.id}`}>{name}</StyledLink>
              </h1>
            </Box>

            <Flex mt={2}>
              <ProgramStatusBadge status={program.status} />
              <Box ml={2} color={SUBTEXT_COLOR}>
                <ProgramDateDisplay
                  status={program.status}
                  targetLaunchDate={program.targetLaunchDate}
                  targetEndDate={program.targetEndDate}
                  closedAt={program.closedAt}
                />
              </Box>
            </Flex>
          </Flex>
          {isShowMenu && (
            <ProgramMenu
              onClick={() => setOpenMenu(!openMenu)}
              program={program}
              status={program.status || ProgramStatus.InDesign}
              isOpen={openMenu}
              onUpdateProgram={function (): void {
                setDialogOpen("update-program");
                setOpenMenu(false);
              }}
              onCloneProgram={function (): void {
                setDialogOpen("clone-program");
                setOpenMenu(false);
              }}
              onArchiveProgram={function (): void {
                setDialogOpen("archive-program");
                setOpenMenu(false);
              }}
              onEndProgram={function (): void {
                setDialogOpen("end-program");
                setOpenMenu(false);
              }}
              onClose={() => setOpenMenu(false)}
            />
          )}
          {dialogOpen && (
            <ModifyProgramDialogs
              dialogOpen={dialogOpen}
              refetch={refetchProgram}
              onClosedDialog={() => {
                setDialogOpen(undefined);
              }}
              selectedProgram={program}
              onProgramArchived={() => {
                navigate("/programs");
              }}
            />
          )}
        </Flex>
        {tabs && <ProgramTabs tabs={tabs} initialTab={initialTab} />}
      </Container>
    </Box>
  );
}
